import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { buildProviders, KalDialogConfig, KalDialogService } from '@kalidea/kaligraphi';
import gql from 'graphql-tag';
import isEqual from 'lodash-es/isEqual';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { CustomFormControlAccessWithLoaders } from 'src/app/_core/tools/custom-form-control-access';
import {
    SharedAddCityDialogComponent, SharedAddCityDialogData
} from 'src/app/_shared/dialogs/shared-add-new-city-dialog/shared-add-city-dialog.component';
import { arrayNotEmpty, arrayOnlyOne } from 'src/app/utils/array.utils';
import { FormValidator } from 'src/app/validators/form-validator';
import { MutationGetAllVilleCpArgs, VilleDto } from 'src/graphql/generated-types';

type SharedCityPickerFormType = {
    cityName: FormControl<string | null>;
    cityId: FormControl<number | null>;
    postalCode: FormControl<string | null>;
    countryName: FormControl<string | null>;
};

@Component({
    selector: 'app-shared-city-picker',
    templateUrl: './shared-city-picker.component.html',
    styleUrls: ['./shared-city-picker.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedCityPickerComponent),
    standalone: false
})
export class SharedCityPickerComponent extends CustomFormControlAccessWithLoaders implements OnInit {

    form: FormGroup<SharedCityPickerFormType>;

    cityList: VilleDto[] = [];

    @Input() id = 'shared_city-picker';

    constructor(private readonly fb: FormBuilder,
                private readonly graphqlService: GraphqlService,
                protected readonly cdr: ChangeDetectorRef,
                private readonly dialogService: KalDialogService) {
        super(cdr);
    }

    get isCountryNameFieldVisible(): boolean {
        const countryName = this.form.value?.countryName;
        return !!countryName && countryName !== 'France';
    }

    get isCityRequired(): boolean {
        return !!this.postalCodeControl.value;
    }

    get postalCodeControl(): FormControl {
        return this.form.get('postalCode') as FormControl;
    }

    private get cityIdControl(): FormControl {
        return this.form.get('cityId') as FormControl;
    }

    validate(c: AbstractControl): Observable<{ citypicker: boolean }> | Observable<null> {
        return of(this.form.valid ? null : {citypicker: true});
    }

    writeValue(value: any): void {
        if (!value && this.form.dirty) {
            this.form.reset({}, {emitEvent: false});
            this.cityList = [];
            return;
        }

        const data = {
            cityId: value?.cityId || null,
            cityName: value?.cityName || null,
            postalCode: value?.postalCode || '',
            countryName: value?.countryName || ''
        };

        this.form.patchValue(data);
    }

    openCityAddDialog(): void {
        const dialogConfig = new KalDialogConfig<SharedAddCityDialogData>({
            title: 'Ajouter une nouvelle ville',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
        });

        this.watch$ = this.dialogService.open(SharedAddCityDialogComponent, dialogConfig).afterClosed.pipe(
            filter(dialogResult => !!dialogResult?.accepted && arrayNotEmpty(this.cityList)),
            switchMap(() => this.wrapLoader$(this.getCityList$(this.postalCodeControl.value)))
        );
    }

    private getCityList$(postalCode: string): Observable<VilleDto[]> {
        if (!postalCode) {
            this.cityList = [];
            return of([]);
        }

        return this.graphqlService.mutateOne<'GetAllVilleCP', MutationGetAllVilleCpArgs>({
            mutation: gql`
                mutation GetAllVilleCP($contexte: ContextInfosInput, $cp: String) {
                    GetAllVilleCP(contexte: $contexte, cp: $cp) {
                        VilleDTO {
                            Id
                            Libelle
                            Pays {
                                Id
                                Libelle
                            }
                        }
                    }
                }
            `,
            variables: {cp: postalCode}
        }).pipe(
            map(GetAllVilleCP => GetAllVilleCP?.VilleDTO || []),
            tap((cityList: VilleDto[]) => this.cityList = cityList)
        );
    }

    private resetCitySelection(): void {
        this.cityList = [];
        // Supprimer les validateurs AVANT patchValue : patchValue déclenche
        // notifyUpdate (via form.valueChanges) et le form doit être valide à ce moment
        this.cityIdControl.clearValidators();
        this.cityIdControl.patchValue(null);
        this.cityIdControl.updateValueAndValidity();
    }

    private initSubscriptions(): void {
        this.watch$ = this.postalCodeControl.valueChanges.pipe(
            tap(postalCodeValue => {
                if (!postalCodeValue) {
                    this.resetCitySelection();
                    return;
                }

                this.cityIdControl.setValidators([FormValidator.isNotNull]);
                this.cityIdControl.updateValueAndValidity();
            }),
            switchMap(postalCodeValue => this.wrapLoader$(this.getCityList$(postalCodeValue))),
            filter(() => arrayOnlyOne(this.cityList)),
            tap(() => this.cityIdControl.patchValue(this.cityList[0].Id))
        );

        this.watch$ = this.cityIdControl.valueChanges.pipe(
            distinctUntilChanged(isEqual),
            tap(cityIdValue => {
                const currentCity = this.cityList.find(c => c.Id === cityIdValue) || null;
                this.form.get('cityName').patchValue(currentCity?.Libelle || null, {emitEvent: false});
            })
        );
    }

    ngOnInit(): void {
        this.form = this.fb.group<SharedCityPickerFormType>({
            cityName: this.fb.control(null),
            cityId: this.fb.control(null),
            postalCode: this.fb.control('', {updateOn: 'blur'}),
            countryName: this.fb.control({value: '', disabled: true}),
        });

        this.initSubscriptions();

        this.watch$ = this.form.valueChanges.pipe(
            startWith(this.form.value),
            distinctUntilChanged(isEqual),
            tap(value => this.notifyUpdate(value)),
        );
    }

}
